import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ChevronRightIcon } from '@rsos/base-assets/icons';
import { Box, Text } from '@rsos/base-components';
import { useFlag } from '@rsos/flags';
import { TRANSCRIPT_TAB } from '@rsos/reduxDataStore/src/features/eventPane/eventPaneTabs';
import useHasAccessToSummarizationAndKeywords from '../../../../../hooks/useHasAccessToSummarizationAndKeywords';
import useHasAccessToTranscriptAndAudioPlayback from '../../../../../hooks/useHasAccessToTranscriptAndAudioPlayback';
import useTrackMissingTranscript from '../../../../../hooks/useTrackMissingTranscript';
import AudioPlayer from '../../AudioPlayer';
import CollaboratorBanner from '../CollaboratorBanner';
import IncidentSummary from './IncidentSummary';
import { TranscriptFeedback } from './TranscriptFeedback';
import TranscriptViewer from './TranscriptViewer';
import { TranscriptContent } from './TranscriptionTab.styles';
import useMissingTranscriptDelay from './hooks/useMissingTranscriptDelay';
import {
  selectPreviousTranscriptionsForCall,
  selectTranscriptionForCall,
} from './transcriptionSlice';
import type { RootState } from './transcriptionSlice/transcriptionRootState';

type AppState = RootState & {
  eventPane: { selectedTab: string };
};

interface TranscriptionTabProps {
  callerId: string;
  onOpenPreviousTranscripts?: () => void;
}

/**
 * The Transcript tab of the EventPane
 */
const TranscriptionTab = ({
  callerId,
  onOpenPreviousTranscripts = () => undefined,
}: TranscriptionTabProps) => {
  const isInternalFeedbackEnabled = useFlag('transcript_feedback_api');
  const showRecentTranscripts = useFlag('show_recent_transcripts');
  const hasAccessToTranscriptionAndAudioPlayback =
    useHasAccessToTranscriptAndAudioPlayback();
  const hasAccessToSummarizationAndKeywords =
    useHasAccessToSummarizationAndKeywords();
  const hasData =
    hasAccessToTranscriptionAndAudioPlayback ||
    hasAccessToSummarizationAndKeywords;

  // Track if transcript tab is active
  const selectedTab = useSelector(
    (state: AppState) => state.eventPane.selectedTab,
  );
  const isTabActive = selectedTab === TRANSCRIPT_TAB;

  // Get call details for tracking
  const callDetails = useSelector(
    (state: AppState) => state.emergencyCalls.callQueue?.[callerId],
  );

  const callStatuses = callDetails?.callStatuses || [];
  const callStatus = callStatuses[callStatuses.length - 1] || null;
  const location0 = callDetails?.core?.locations?.[0] as unknown;
  const callStartTime = (() => {
    if (!location0 || typeof location0 !== 'object') return null;
    const loc = location0 as Record<string, unknown>;
    const conversationStart = loc.conversation_start_time;
    if (typeof conversationStart === 'number') return conversationStart;
    const callStart = loc.call_start_time;
    if (typeof callStart === 'number') return callStart;
    return null;
  })();

  // Track missing transcripts
  useTrackMissingTranscript(callerId, isTabActive, callStatus, callStartTime);

  const isTranscriptMaximized = useSelector(
    (state: AppState) => !!state?.transcriptions?.maximized,
  );

  const shouldRenderSummary =
    !isTranscriptMaximized && hasAccessToSummarizationAndKeywords;

  const currentTranscription = useSelector((state: AppState) =>
    selectTranscriptionForCall(callerId)(state),
  );
  const totalRecords = useSelector(
    (state: AppState) =>
      (state as any)?.transcriptions?.transcriptionTotals?.[callerId],
  ) as number | undefined;
  const previousCount = useSelector(
    (state: AppState) =>
      selectPreviousTranscriptionsForCall(callerId)(state).length,
  );

  const seededCallStartTime =
    currentTranscription?.payload?.call_information?.call_start_time || null;
  const hasCurrentTranscriptionSeeded =
    typeof callStartTime === 'number' &&
    typeof seededCallStartTime === 'number' &&
    seededCallStartTime > 0 &&
    Math.abs(callStartTime - seededCallStartTime) < 10 * 60 * 1000;

  const previousTotalCount =
    typeof totalRecords === 'number'
      ? Math.max(0, totalRecords - (hasCurrentTranscriptionSeeded ? 1 : 0))
      : previousCount;

  const hasPreviousTranscriptions = previousTotalCount > 0;

  const isFetching = !!currentTranscription?.fetching;
  const tokenCount = currentTranscription?.payload?.tokens?.length || 0;

  const hasWaitedForTranscript = useMissingTranscriptDelay({
    callerId,
    callStartTime,
    isFetching,
    tokenCount,
  });

  const showMissingCurrentTranscriptMessage = useMemo(() => {
    if (isFetching) return false;
    return tokenCount === 0 && hasWaitedForTranscript;
  }, [isFetching, tokenCount, hasWaitedForTranscript]);

  return (
    <Box gridRowGap="16px" height="100%">
      <CollaboratorBanner />
      {((shouldRenderSummary && callerId) || isInternalFeedbackEnabled) && (
        <TranscriptFeedback callerId={callerId} />
      )}
      {hasAccessToTranscriptionAndAudioPlayback && (
        <AudioPlayer callerId={callerId} />
      )}
      {shouldRenderSummary && <IncidentSummary callerId={callerId} />}

      <TranscriptContent>
        {hasData ? (
          <>
            {hasAccessToTranscriptionAndAudioPlayback &&
              showRecentTranscripts &&
              hasPreviousTranscriptions &&
              !showMissingCurrentTranscriptMessage && (
                <Box
                  horizontal
                  align="center"
                  gridColumnGap="6px"
                  style={{ cursor: 'pointer' }}
                  onClick={() => onOpenPreviousTranscripts()}
                  data-name="open-previous-transcripts-cta"
                  aria-label="Open recent transcripts"
                >
                  <Text
                    size="normal"
                    color="primaryBase"
                    fontWeight="semibold"
                    style={{ textDecoration: 'underline' }}
                  >
                    Open recent transcripts ({previousTotalCount})
                  </Text>
                  <ChevronRightIcon
                    width={16}
                    height={16}
                    color="primaryBase"
                  />
                </Box>
              )}
            {hasAccessToTranscriptionAndAudioPlayback && (
              <>
                {showMissingCurrentTranscriptMessage ? (
                  <Box
                    border="1px solid"
                    borderColor="heavyLineOutline"
                    borderRadius="8px"
                    bg="background"
                    px="16px"
                    py="14px"
                    gridRowGap="10px"
                    align="center"
                    style={
                      showRecentTranscripts && hasPreviousTranscriptions
                        ? { cursor: 'pointer' }
                        : undefined
                    }
                    onClick={
                      showRecentTranscripts && hasPreviousTranscriptions
                        ? () => onOpenPreviousTranscripts()
                        : undefined
                    }
                    data-name="no-transcript-cta"
                  >
                    <Text
                      size="normal"
                      textAlign="center"
                      color="secondaryText"
                    >
                      No transcript is available for this call yet.
                    </Text>
                    <Text
                      size="normal"
                      textAlign="center"
                      color="secondaryText"
                    >
                      This can happen due to a hangup or an upstream error.
                    </Text>
                    {showRecentTranscripts && hasPreviousTranscriptions && (
                      <Box horizontal align="center" gridColumnGap="6px">
                        <Text
                          size="normal"
                          color="primaryBase"
                          fontWeight="semibold"
                          style={{ textDecoration: 'underline' }}
                        >
                          Open recent transcripts ({previousTotalCount})
                        </Text>
                        <ChevronRightIcon
                          width={16}
                          height={16}
                          color="primaryBase"
                        />
                      </Box>
                    )}
                  </Box>
                ) : (
                  <TranscriptViewer
                    isTranscriptMaximized={isTranscriptMaximized}
                    callerId={callerId}
                  />
                )}
              </>
            )}
          </>
        ) : (
          <Text textAlign="center" color="secondaryText">
            No Audio or Transcript available
          </Text>
        )}
      </TranscriptContent>
    </Box>
  );
};

export default TranscriptionTab;
